/**
 * Cartridge Widget Renderer
 *
 * Renders dashboard widgets provided by active cartridges.
 * Uses a widget registry for component lookup with fallback placeholder.
 */
import { Suspense, lazy, ComponentType } from 'react'
import { PuzzlePieceIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import type { CartridgeDashboardWidgetWithSource } from '../../hooks/useCartridgeUI'
import { useTranslation } from '../../hooks/useTranslation'

// Widget registry - cartridges register their widget components here
type WidgetComponent = ComponentType<{ widget: CartridgeDashboardWidgetWithSource }>
const widgetRegistry = new Map<string, WidgetComponent>()

/**
 * Register a widget component for a cartridge
 * Call this at cartridge initialization time
 */
export function registerCartridgeWidget(
  cartridgeSlug: string,
  widgetKey: string,
  component: WidgetComponent
) {
  const fullKey = `${cartridgeSlug}:${widgetKey}`
  widgetRegistry.set(fullKey, component)
}

/**
 * Get a registered widget component
 */
export function getCartridgeWidget(
  cartridgeSlug: string,
  widgetKey: string
): WidgetComponent | undefined {
  const fullKey = `${cartridgeSlug}:${widgetKey}`
  return widgetRegistry.get(fullKey)
}

// Size classes for different widget sizes
const sizeClasses: Record<string, string> = {
  small: 'col-span-1',
  medium: 'col-span-1 sm:col-span-2',
  large: 'col-span-1 sm:col-span-2 lg:col-span-2',
  full: 'col-span-full',
}

interface WidgetPlaceholderProps {
  widget: CartridgeDashboardWidgetWithSource
}

/**
 * Placeholder shown when widget component is not registered
 */
function WidgetPlaceholder({ widget }: WidgetPlaceholderProps) {
  const { t } = useTranslation()

  return (
    <div className="h-full flex flex-col items-center justify-center p-6 text-center">
      <PuzzlePieceIcon className="h-12 w-12 text-gray-300 mb-3" />
      <p className="text-sm font-medium text-gray-500">
        {t(widget.widget.titleKey as never) || widget.widget.key}
      </p>
      <p className="text-xs text-gray-400 mt-1">
        {widget.cartridgeSlug}
      </p>
    </div>
  )
}

/**
 * Error boundary fallback for widget errors
 */
function WidgetError({ widget }: WidgetPlaceholderProps) {
  const { t } = useTranslation()

  return (
    <div className="h-full flex flex-col items-center justify-center p-6 text-center">
      <ExclamationTriangleIcon className="h-12 w-12 text-amber-400 mb-3" />
      <p className="text-sm font-medium text-gray-500">
        {t('cartridges.widgetError' as never) || 'Widget Error'}
      </p>
      <p className="text-xs text-gray-400 mt-1">
        {widget.widget.key}
      </p>
    </div>
  )
}

/**
 * Loading skeleton for widgets
 */
function WidgetSkeleton() {
  return (
    <div className="h-full animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
      <div className="space-y-3">
        <div className="h-3 bg-gray-200 rounded"></div>
        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
        <div className="h-3 bg-gray-200 rounded w-4/6"></div>
      </div>
    </div>
  )
}

interface CartridgeWidgetRendererProps {
  widget: CartridgeDashboardWidgetWithSource
}

/**
 * Renders a single cartridge widget
 */
export function CartridgeWidgetRenderer({ widget }: CartridgeWidgetRendererProps) {
  const { t } = useTranslation()
  const sizeClass = sizeClasses[widget.widget.size] || sizeClasses.small

  // Look up the registered component
  const WidgetComponent = getCartridgeWidget(widget.cartridgeSlug, widget.widget.key)

  return (
    <div
      className={`${sizeClass} bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5`}
    >
      <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
        <PuzzlePieceIcon className="h-4 w-4 text-gray-400" />
        {t(widget.widget.titleKey as never) || widget.widget.key}
      </h3>
      <div className="min-h-[120px]">
        {WidgetComponent ? (
          <Suspense fallback={<WidgetSkeleton />}>
            <WidgetComponent widget={widget} />
          </Suspense>
        ) : (
          <WidgetPlaceholder widget={widget} />
        )}
      </div>
    </div>
  )
}

interface CartridgeWidgetsGridProps {
  widgets: CartridgeDashboardWidgetWithSource[]
}

/**
 * Renders a grid of cartridge dashboard widgets
 */
export function CartridgeWidgetsGrid({ widgets }: CartridgeWidgetsGridProps) {
  const { t } = useTranslation()

  if (widgets.length === 0) {
    return null
  }

  return (
    <div className="mt-8">
      <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
        <PuzzlePieceIcon className="h-5 w-5 text-gray-400" />
        {t('cartridges.widgets' as never) || 'Cartridge Widgets'}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {widgets.map((widget) => (
          <CartridgeWidgetRenderer
            key={`${widget.cartridgeSlug}:${widget.widget.key}`}
            widget={widget}
          />
        ))}
      </div>
    </div>
  )
}
